// ========================================
// PAYMENT SERVICE - CLEAN IMPLEMENTATION
// Zentrale Zahlungsverwaltung
// ========================================

import invoiceService from './invoiceService';
import { isValidOutflowPaymentMethod } from './paymentMethodService';
import {
  resolveInvoiceRemainingAmount,
  isChildOfActiveConsolidated
} from '../domain/suppliers/balance';
import {
  getAchatDisplayId,
  resolveLiveChildInvoice as resolveLiveChildInvoiceShared,
  refMatchesInvoice
} from '../domain/purchases/consolidatedInvoiceRefs';

class PaymentService {
  constructor() {
    // Align with Quick Sale: only 'cash' and 'electronic'
    this.paymentMethods = {
      cash: { nameBound: 'Espèces', name: 'Espèces', nameAr: 'نقداً' },
      electronic: { nameBound: 'Électronique', name: 'Électronique', nameAr: 'إلكتروني' }
    };
  }

  /**
   * Erstellt eine neue Zahlung
   */
  createPayment(invoiceId, amount, method, notes = '', receiptMeta = null, paymentId = null) {
    const payment = {
      id: paymentId || `payment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      invoiceId,
      amount,
      method,
      notes,
      createdAt: new Date().toISOString(),
      status: 'completed'
    };

    if (receiptMeta?.receiptUrl) {
      payment.receiptUrl = receiptMeta.receiptUrl;
      payment.receiptFileName = receiptMeta.receiptFileName || null;
      if (receiptMeta.receiptMimeType) {
        payment.receiptMimeType = receiptMeta.receiptMimeType;
      }
    }

    return payment;
  }

  /**
   * Verarbeitet eine Zahlung für eine Rechnung
   */
  processPayment(invoice, paymentAmount, paymentMethod, notes = '', receiptMeta = null, options = {}) {
    try {
      const payment = this.createPayment(
        invoice.id,
        paymentAmount,
        paymentMethod,
        notes,
        receiptMeta,
        options.paymentId || null
      );
      
      // Berechne neuen Status
      const newPaidAmount = (invoice.paidAmount || 0) + paymentAmount;
      const updatedInvoice = {
        ...invoice,
        paidAmount: newPaidAmount,
        lastPaymentDate: new Date().toISOString(),
        payments: [...(invoice.payments || []), payment]
      };
      const remainingAmount = resolveInvoiceRemainingAmount(updatedInvoice);
      updatedInvoice.status = remainingAmount <= 0 ? 'paid' : 'partial';

      return {
        success: true,
        invoice: updatedInvoice,
        payment,
        remainingAmount
      };
    } catch (error) {
      console.error('❌ [PAYMENT SERVICE] Error processing payment:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  isConsolidatedInvoice(invoice) {
    return invoiceService.isConsolidatedInvoice(invoice);
  }

  /** Resolve CON ref → live Achat invoice (id / purchaseId only — no facture-number match). */
  resolveLiveChildInvoice(ref, allInvoices = []) {
    return resolveLiveChildInvoiceShared(ref, allInvoices);
  }

  refMatchesInvoice(ref, invoice) {
    return refMatchesInvoice(ref, invoice);
  }

  /** Facture achat rattachée à une facture consolidée encore active (paiement via le parent). */
  isChildOfConsolidated(invoice, invoices = []) {
    return isChildOfActiveConsolidated(invoice, invoices);
  }

  /** Pay consolidated parent when child is selected; otherwise the invoice itself. */
  resolvePayableInvoice(invoice, invoices = []) {
    if (!invoice) return null;
    if (this.isConsolidatedInvoice(invoice)) return invoice;
    if (!this.isChildOfConsolidated(invoice, invoices)) return invoice;

    const parent = (invoices || []).find((candidate) => {
      if (!this.isConsolidatedInvoice(candidate)) return false;
      if (
        candidate.supplierId != null &&
        invoice.supplierId != null &&
        String(candidate.supplierId) !== String(invoice.supplierId)
      ) {
        return false;
      }
      return (candidate.originalInvoices || []).some((ref) => this.refMatchesInvoice(ref, invoice));
    });
    return parent || invoice;
  }

  /** Open purchase/order invoices only (one row per achat) — payment overview page. */
  filterIndividualOpenInvoices(invoices = []) {
    const list = Array.isArray(invoices) ? invoices : [];
    return list.filter((invoice) => {
      if (this.isConsolidatedInvoice(invoice)) return false;
      return this.calculateRemainingAmount(invoice) > 0;
    });
  }

  /** Open invoices for payment UI — excludes children when a consolidated invoice exists. */
  filterPayableInvoices(invoices = []) {
    const list = Array.isArray(invoices) ? invoices : [];
    return list.filter((invoice) => {
      const remaining = this.calculateRemainingAmount(invoice);
      if (remaining <= 0) return false;
      if (this.isChildOfConsolidated(invoice, list)) return false;
      return true;
    });
  }

  /** Chronological sort (oldest first) for consolidated child invoices. */
  sortOriginalsFifo(invoices = [], originalIds = []) {
    return (originalIds || [])
      .map((id) => this.resolveLiveChildInvoice({ id }, invoices))
      .filter(Boolean)
      .sort((a, b) => {
        const dateA = a.invoiceDate ? new Date(a.invoiceDate) :
          (a.createdAt ? new Date(a.createdAt) : new Date(0));
        const dateB = b.invoiceDate ? new Date(b.invoiceDate) :
          (b.createdAt ? new Date(b.createdAt) : new Date(0));
        if (dateA.getTime() !== dateB.getTime()) {
          return dateA - dateB;
        }
        const numA = a.invoiceNumber || a.id || '';
        const numB = b.invoiceNumber || b.id || '';
        return numA.localeCompare(numB);
      });
  }

  /** Display id for CON children = live Achat purchaseId (Achats page). */
  getAchatDisplayId(invoiceOrRef) {
    return getAchatDisplayId(invoiceOrRef);
  }

  /**
   * Child rows for CON UI — displayNumber is the live Achat id (Achats page).
   */
  getConsolidatedChildRows(consolidated, allInvoices = []) {
    if (!this.isConsolidatedInvoice(consolidated)) return [];
    const refs = consolidated.originalInvoices || [];
    const seen = new Set();
    const rows = [];

    for (const ref of refs) {
      const live = this.resolveLiveChildInvoice(ref, allInvoices);
      const inv = live || {
        id: ref?.id,
        purchaseId: ref?.purchaseId,
        originalTransactionId: ref?.originalTransactionId,
        invoiceNumber: ref?.invoiceNumber,
        invoiceDate: ref?.invoiceDate,
        totalAmount: ref?.amount ?? ref?.totalAmount ?? 0,
        paidAmount: ref?.paidAmount || 0,
        payments: []
      };
      const purchaseId =
        this.getAchatDisplayId(inv) ||
        this.getAchatDisplayId(ref) ||
        (ref?.id && String(ref.id).startsWith('ACHAT') ? String(ref.id) : null);
      const key = String(inv.id || purchaseId || inv.invoiceNumber || '');
      if (!key || seen.has(key)) continue;
      seen.add(key);
      // CON UI shows Achat ids (Achats page), not FACT / 2026.xxxxx facture numbers
      const displayNumber = purchaseId || inv.invoiceNumber || inv.id || ref?.invoiceNumber;
      rows.push({
        id: inv.id || ref?.id,
        purchaseId: purchaseId || null,
        invoiceNumber: displayNumber,
        factureNumber: inv.invoiceNumber || ref?.invoiceNumber || null,
        invoiceDate: inv.invoiceDate || inv.createdAt || ref?.invoiceDate,
        totalAmount: inv.totalAmount ?? ref?.amount ?? 0,
        paidAmount: inv.paidAmount ?? ref?.paidAmount ?? 0,
        remainingAmount: this.calculateRemainingAmount(inv),
        status: this.getInvoiceStatus(inv),
        payments: Array.isArray(inv.payments) ? inv.payments : [],
        lastPaymentDate: inv.lastPaymentDate || null
      });
    }

    return rows.sort((a, b) => {
      const dateA = a.invoiceDate ? new Date(a.invoiceDate).getTime() : 0;
      const dateB = b.invoiceDate ? new Date(b.invoiceDate).getTime() : 0;
      if (dateA !== dateB) return dateA - dateB;
      return String(a.invoiceNumber || '').localeCompare(String(b.invoiceNumber || ''));
    });
  }

  getConsolidatedNumbersLabel(consolidated, allInvoices = []) {
    const rows = this.getConsolidatedChildRows(consolidated, allInvoices);
    return rows
      .map((r) => r.purchaseId || r.invoiceNumber)
      .filter(Boolean)
      .join(', ');
  }

  getOldestOpenChild(consolidated, allInvoices = []) {
    return this.getConsolidatedChildRows(consolidated, allInvoices)
      .find((row) => row.remainingAmount > 0) || null;
  }

  recalculateConsolidatedFromChildren(updated, consolidatedId, originalIds, targetInvoice) {
    const newPaidTotal = originalIds.reduce((sum, id) => {
      const inv = this.resolveLiveChildInvoice({ id }, updated);
      return sum + (inv?.paidAmount || 0);
    }, 0);
    const consolidated = updated.find((i) => i.id === consolidatedId);
    if (!consolidated) return;
    consolidated.paidAmount = newPaidTotal;
    consolidated.status = newPaidTotal >= (consolidated.totalAmount || 0)
      ? 'paid'
      : (newPaidTotal > 0 ? 'partial' : 'pending');
    if (Array.isArray(targetInvoice?.payments)) {
      consolidated.payments = targetInvoice.payments;
    }
    if (targetInvoice?.lastPaymentDate) {
      consolidated.lastPaymentDate = targetInvoice.lastPaymentDate;
    }
  }

  applyFifoToOriginals(updated, originalIds, paymentAmount, paymentMeta = {}) {
    const originalInvoices = this.sortOriginalsFifo(updated, originalIds);
    let remainingToApply = paymentAmount;
    const now = paymentMeta.createdAt || new Date().toISOString();
    for (const inv of originalInvoices) {
      const remaining = this.calculateRemainingAmount(inv);
      if (remaining <= 0) continue;
      const apply = Math.min(remainingToApply, remaining);
      if (apply > 0) {
        const payment = this.createPayment(
          inv.id,
          apply,
          paymentMeta.method || 'cash',
          paymentMeta.notes || ''
        );
        if (paymentMeta.id) {
          payment.consolidatedMirror = true;
          payment.sourcePaymentId = paymentMeta.id;
        }
        payment.createdAt = now;
        inv.paidAmount = (inv.paidAmount || 0) + apply;
        inv.status = inv.paidAmount >= inv.totalAmount ? 'paid' : (inv.paidAmount > 0 ? 'partial' : 'pending');
        inv.payments = [...(inv.payments || []), payment];
        inv.lastPaymentDate = now;
        remainingToApply -= apply;
        if (remainingToApply <= 0) break;
      }
    }
    return remainingToApply;
  }

  applyTargetToOriginal(updated, targetOriginalId, paymentAmount, paymentMeta = {}) {
    const child =
      this.resolveLiveChildInvoice({ id: targetOriginalId }, updated) ||
      updated.find((i) => i.id === targetOriginalId);
    if (!child) return 0;
    const remaining = this.calculateRemainingAmount(child);
    const apply = Math.min(paymentAmount, remaining);
    if (apply > 0) {
      const now = paymentMeta.createdAt || new Date().toISOString();
      const payment = this.createPayment(
        child.id,
        apply,
        paymentMeta.method || 'cash',
        paymentMeta.notes || ''
      );
      if (paymentMeta.id) {
        payment.consolidatedMirror = true;
        payment.sourcePaymentId = paymentMeta.id;
      }
      payment.createdAt = now;
      child.paidAmount = (child.paidAmount || 0) + apply;
      child.status = child.paidAmount >= child.totalAmount ? 'paid' : (child.paidAmount > 0 ? 'partial' : 'pending');
      child.payments = [...(child.payments || []), payment];
      child.lastPaymentDate = now;
    }
    return apply;
  }

  validateConsolidatedPayment(invoice, amount, method, { mode = 'fifo', targetOriginalId } = {}, allInvoices = []) {
    const base = this.validatePayment(invoice, amount, method);
    if (!base.isValid) return base;
    if (!this.isConsolidatedInvoice(invoice)) return base;

    if (mode === 'target') {
      if (!targetOriginalId) {
        return { isValid: false, errors: ['Sélectionnez une facture source'] };
      }
      const child = this.getConsolidatedChildRows(invoice, allInvoices)
        .find((row) => row.id === targetOriginalId);
      if (!child) {
        return { isValid: false, errors: ['Facture source introuvable'] };
      }
      if (child.remainingAmount <= 0) {
        return { isValid: false, errors: ['Cette facture est déjà payée'] };
      }
      if (amount > child.remainingAmount) {
        return {
          isValid: false,
          errors: [`Le montant dépasse le restant de la facture (${child.invoiceNumber})`]
        };
      }
    }

    return base;
  }

  /**
   * Propagates payment from a consolidated invoice to its original invoices,
   * or aggregates payment from original invoices to the consolidated one.
   * options.mode: 'fifo' | 'target'
   */
  syncConsolidationPayment(targetInvoice, paymentAmount, invoices, options = {}) {
    const mode = options.mode === 'target' ? 'target' : 'fifo';
    const targetOriginalId = options.targetOriginalId;

    if (this.isConsolidatedInvoice(targetInvoice)) {
      const originalIds = (targetInvoice.originalInvoices || []).map((o) => o.id);
      const updated = invoices.map((inv) => ({ ...inv }));
      const latestPayment = Array.isArray(targetInvoice.payments) && targetInvoice.payments.length > 0
        ? targetInvoice.payments[targetInvoice.payments.length - 1]
        : null;
      const paymentMeta = {
        id: latestPayment?.id || null,
        method: latestPayment?.method || 'cash',
        notes: latestPayment?.notes || '',
        createdAt: latestPayment?.createdAt || targetInvoice.lastPaymentDate || new Date().toISOString()
      };

      if (mode === 'target' && targetOriginalId) {
        this.applyTargetToOriginal(updated, targetOriginalId, paymentAmount, paymentMeta);
      } else {
        this.applyFifoToOriginals(updated, originalIds, paymentAmount, paymentMeta);
      }

      this.recalculateConsolidatedFromChildren(updated, targetInvoice.id, originalIds, targetInvoice);
      return updated;
    }

    // If paying a normal invoice: merge the already-processed invoice (paidAmount, payments, status),
    // then update consolidated parent (if any) from the sum of originals — ohne Merge wäre paidSum falsch.
    const updated = invoices.map((inv) =>
      inv.id === targetInvoice.id ? { ...inv, ...targetInvoice } : { ...inv }
    );
    // Try to find a consolidated invoice for same supplier that references originals
    const consolidatedForSupplier = updated.find(
      (i) =>
        this.isConsolidatedInvoice(i) &&
        i.supplierId === targetInvoice.supplierId &&
        Array.isArray(i.originalInvoices) &&
        i.originalInvoices.some((ref) => ref?.id === targetInvoice.id)
    );
    if (consolidatedForSupplier && Array.isArray(consolidatedForSupplier.originalInvoices)) {
      const originalIds = new Set(consolidatedForSupplier.originalInvoices.map(o => o.id));
      const paidSum = updated
        .filter(i => originalIds.has(i.id))
        .reduce((sum, i) => sum + (i.paidAmount || 0), 0);
      consolidatedForSupplier.paidAmount = paidSum;
      consolidatedForSupplier.status = paidSum >= (consolidatedForSupplier.totalAmount || 0) ? 'paid' : (paidSum > 0 ? 'partial' : 'pending');
    }

    return updated;
  }

  /**
   * Berechnet den Restbetrag einer Rechnung
   */
  calculateRemainingAmount(invoice) {
    // ADR-0013: domain/suppliers/balance
    return resolveInvoiceRemainingAmount(invoice);
  }

  /**
   * Prüft ob eine Rechnung vollständig bezahlt ist
   */
  isInvoicePaid(invoice) {
    return this.calculateRemainingAmount(invoice) <= 0;
  }

  /**
   * Prüft ob eine Rechnung teilweise bezahlt ist
   */
  isInvoicePartiallyPaid(invoice) {
    const paidAmount = invoice.paidAmount || 0;
    const remainingAmount = this.calculateRemainingAmount(invoice);
    return paidAmount > 0 && remainingAmount > 0;
  }

  /**
   * Gibt den Status einer Rechnung zurück
   */
  getInvoiceStatus(invoice) {
    if (this.isInvoicePaid(invoice)) {
      return 'paid';
    } else if (this.isInvoicePartiallyPaid(invoice)) {
      return 'partial';
    } else {
      return 'pending';
    }
  }

  /**
   * Gruppiert Zahlungen nach Methode
   */
  groupPaymentsByMethod(payments) {
    const grouped = {};
    
    payments.forEach(payment => {
      if (!grouped[payment.method]) {
        grouped[payment.method] = {
          method: payment.method,
          count: 0,
          totalAmount: 0,
          payments: []
        };
      }
      
      grouped[payment.method].count++;
      grouped[payment.method].totalAmount += payment.amount;
      grouped[payment.method].payments.push(payment);
    });

    return grouped;
  }

  /**
   * Berechnet Statistiken für Zahlungen
   */
  calculatePaymentStats(invoices) {
    const stats = {
      totalInvoices: invoices.length,
      paidInvoices: 0,
      partialInvoices: 0,
      pendingInvoices: 0,
      totalAmount: 0,
      totalPaid: 0,
      totalRemaining: 0,
      paymentMethods: {}
    };

    invoices.forEach(invoice => {
      const status = this.getInvoiceStatus(invoice);
      
      switch (status) {
        case 'paid':
          stats.paidInvoices++;
          break;
        case 'partial':
          stats.partialInvoices++;
          break;
        case 'pending':
          stats.pendingInvoices++;
          break;
      }

      stats.totalAmount += invoice.totalAmount;
      stats.totalPaid += invoice.paidAmount || 0;
      stats.totalRemaining += this.calculateRemainingAmount(invoice);

      // Gruppiere Zahlungsmethoden
      if (invoice.payments) {
        invoice.payments.forEach(payment => {
          if (!stats.paymentMethods[payment.method]) {
            stats.paymentMethods[payment.method] = {
              count: 0,
              totalAmount: 0
            };
          }
          stats.paymentMethods[payment.method].count++;
          stats.paymentMethods[payment.method].totalAmount += payment.amount;
        });
      }
    });

    return stats;
  }

  /**
   * Validiert eine Zahlung
   */
  validatePayment(invoice, amount, method) {
    const errors = [];

    if (!invoice) {
      errors.push('Rechnung nicht gefunden');
      return { isValid: false, errors };
    }

    if (!amount || amount <= 0) {
      errors.push('Betrag muss größer als 0 sein');
    }

    if (amount > this.calculateRemainingAmount(invoice)) {
      errors.push('Betrag darf nicht größer als der Restbetrag sein');
    }

    // UI sends provider ids (bankily, sedad, …) — not only cash|electronic
    if (!method || !isValidOutflowPaymentMethod(method)) {
      errors.push('Ungültige Zahlungsmethode');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Gibt verfügbare Zahlungsmethoden zurück
   */
  getPaymentMethods(language = 'fr') {
    return Object.keys(this.paymentMethods).map(key => ({
      value: key,
      label: language === 'ar' ? this.paymentMethods[key].nameAr : this.paymentMethods[key].name
    }));
  }
}

export default new PaymentService();